// src/utils/upload.js - the one upload request used by every media uploader (audit FL-39).
//
// Goes through the shared axios client, so the token header, the 401 handling
// (`auth:expired`) and the Czech error texts are the same as for every other call.
// Progress, speed and ETA come from axios' upload progress events.
import api, { MESSAGES, toApiError } from '../services/api';

/** How to make a Grand Finále background video smaller (no "720p" advice - it must be 3584 × 1024 px). */
export const BACKGROUND_VIDEO_ADVICE =
  'Video pozadí uložte ve formátu MP4 v rozlišení 3584 × 1024 px, případně ho zkomprimujte (např. v aplikaci HandBrake).';

export const UPLOAD_MESSAGES = {
  network:
    'Nahrávání se přerušilo – spojení se serverem bylo ztraceno. Zkontrolujte připojení (ideálně Wi-Fi), ' +
    'během nahrávání nezamykejte obrazovku a zkuste to znovu.',
  cancelled: 'Nahrávání zrušeno.',
  locked: 'Tuto choreografii už nelze upravovat. Potřebujete změnu? Napište nám na info@bestofthebest.cz.',
  videoTooLarge:
    'Soubor je příliš velký. Maximální velikost je 4 GB. Video zkraťte nebo zkomprimujte ' +
    '(např. v aplikaci HandBrake), případně ho z mobilu uložte v nižší kvalitě (1080p místo 4K).',
  backgroundTooLarge: `Soubor je příliš velký. Maximální velikost je 4 GB. ${BACKGROUND_VIDEO_ADVICE}`,
};

/** Error thrown when the user cancels an upload (not shown as a failure). */
export class UploadCancelled extends Error {
  constructor() {
    super(UPLOAD_MESSAGES.cancelled);
    this.name = 'UploadCancelled';
    this.cancelled = true;
  }
}

/**
 * POST `file` as multipart form data to `path` (relative to the API base).
 * `onProgress(percent, {loaded, total, rate, estimated})` is called while sending.
 * Resolves with the JSON response; rejects with an ApiError (Czech message) or
 * UploadCancelled when `signal` aborts it.
 */
export async function uploadFile(path, file, { onProgress, signal, fieldName = 'file', tooLargeMessage } = {}) {
  const form = new FormData();
  form.append(fieldName, file);
  try {
    const response = await api.post(path, form, {
      signal,
      timeout: 0,
      onUploadProgress: (event) => {
        if (!onProgress) return;
        const total = event.total || file.size || 0;
        const loaded = event.loaded || 0;
        const percent = total ? Math.min(100, Math.round((loaded * 100) / total)) : 0;
        onProgress(percent, { loaded, total, rate: event.rate, estimated: event.estimated });
      },
    });
    return response.data || {};
  } catch (error) {
    if (signal && signal.aborted) throw new UploadCancelled();
    const apiError = toApiError(error);
    if (apiError.status === 401) apiError.message = MESSAGES.expired;
    else if (apiError.status === 413 && tooLargeMessage) apiError.message = tooLargeMessage;
    else if (apiError.status === null && apiError.message === MESSAGES.network) apiError.message = UPLOAD_MESSAGES.network;
    else if (apiError.status === 403 && apiError.message === MESSAGES.forbidden) apiError.message = UPLOAD_MESSAGES.locked;
    throw apiError;
  }
}

/** "1,5 GB", "820 MB", "12 kB" */
export function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  const units = ['B', 'kB', 'MB', 'GB'];
  let n = value;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  const digits = i >= 2 && n < 10 ? 1 : 0;
  return `${n.toLocaleString('cs-CZ', { maximumFractionDigits: digits, minimumFractionDigits: digits })} ${units[i]}`;
}

/** "zbývá asi 3 min" / "zbývá asi 40 s" */
export function formatEta(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return `zbývá asi ${Math.max(5, Math.round(seconds / 5) * 5)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `zbývá asi ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `zbývá asi ${hours} h ${minutes % 60} min`;
}
